import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import { enableProdMode, LOCALE_ID } from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  NoPreloading,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { CACHE_TABLES } from '@school-dashboard/cache-keys';
import { provideUsersStore } from '@school-dashboard/data-access-users';
import { environment } from '@school-dashboard/environments';
import { provideNotifications } from '@school-dashboard/notifications';
import { checkUserRoles } from '@school-dashboard/permissions';
import { jsonLdInterceptor, TtPreloadingStrategy } from '@techniek-team/common';
import {
  authWithWhitelistedRoutesInterceptor,
  LyceoUser,
  provideTtAuth,
  UserService,
  withClearStorageOnImpersonator,
  withLocalStorage,
  withRoleHierarchy,
  withUserRoles,
} from '@techniek-team/oauth';
import { PermissionService, provideTtPermissions } from '@techniek-team/permissions';
import {
  provideSentry,
  SentryEnvironment,
  withTracing,
  withUserFeedback,
} from '@techniek-team/sentry-web';
import { Drivers, provideClearCacheOnMajorUpgrade, provideStorage } from '@techniek-team/storage';
import { setDefaultOptions } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MainComponent } from './main.component';
import { ROUTES } from './main.routing';

registerLocaleData(localeNl, 'nl');
setDefaultOptions({
  locale: nl,
  weekStartsOn: 1,
});

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(MainComponent, {
  providers: [
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: MAT_DATE_LOCALE, useValue: nl },
    provideHttpClient(withInterceptors([jsonLdInterceptor, authWithWhitelistedRoutesInterceptor])),
    provideSignalFormsConfig({
      classes: {
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-valid': ({ state }) => state().valid(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-invalid': ({ state }) => state().invalid(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-touched': ({ state }) => state().touched(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-dirty': ({ state }) => state().dirty(),
      },
    }),
    provideRouter(
      ROUTES,
      withPreloading(environment.production ? TtPreloadingStrategy : NoPreloading),
      withComponentInputBinding(),
      withViewTransitions(),
    ),
    provideDateFnsAdapter(),
    provideIonicAngular({
      useSetInputAPI: true,
    }),
    provideClearCacheOnMajorUpgrade('__school-dashboard', environment.release),
    provideStorage({
      name: '__school-dashboard',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      tables: [
        'application',
        CACHE_TABLES.ngrxCache,
        CACHE_TABLES.ngrxCacheSchoolClasses,
        CACHE_TABLES.ngrxCacheLocationMembers,
        CACHE_TABLES.ngrxCacheSelectedProductTypesAtLocation,
        CACHE_TABLES.ngrxCacheActivePupilUploads,
      ],
    }),
    provideSentry(
      {
        environment: environment.environment,
        dsn: 'https://f341ed93fc24409b8a408176e636449e@errors.techniek-team.nl//145',
        release: `nl.lyceo.schooldashboard@${environment.release}`,
        autoSessionTracking: true,
        enableTracing: false,
        userService: UserService<LyceoUser>,
        tracePropagationTargets: [environment.coreApiUrl, environment.dashboardApiUrl],
        ignoreErrors: ['Handled unknown error'],
      },
      withTracing(),
      withUserFeedback({
        colorScheme: 'system',
        autoInject: false,
        useSentryUser: {
          name: 'username',
          email: 'email',
        },
      }),
    ),
    provideTtAuth(
      {
        scope: 'openid profile email',
        authority: environment.ssoConfig.authority,
        redirectUrl: environment.ssoConfig.redirectUri,
        postLogoutRedirectUri: `${environment.ssoConfig.redirectUri}/login`,
        unauthorizedRoute: '/login',
        forbiddenRoute: '/geen-toegang',
        maxIdTokenIatOffsetAllowedInSeconds: 300,
        triggerRefreshWhenIdTokenExpired: true,
        whitelist: ['amazon'],
        clientId: environment.ssoConfig.clientId,
        secureRoutes: [environment.coreApiUrl, environment.dashboardApiUrl],
      },
      withLocalStorage('__school-dashboard'),
      withUserRoles(checkUserRoles, [PermissionService]),
      withClearStorageOnImpersonator(),
      withRoleHierarchy({
        ROLE_SKOLEO_CORE_ADMIN: [
          'ADMIN',
          'ROLE_SKOLEO_CORE_READ_ONLY_ADMIN',
          'ROLE_SKOLEO_CORE_COORDINATOR',
          'ROLE_SKOLEO_CORE_SCHOOLCONTACT',
        ],
        ROLE_SKOLEO_CORE_READ_ONLY_ADMIN: 'READ_ONLY_ADMIN',
        ROLE_SKOLEO_CORE_COORDINATOR: 'COORDINATOR',
        ROLE_SKOLEO_CORE_SCHOOLCONTACT: 'SCHOOL_CONTACT',
      }),
    ),
    provideTtPermissions({
      roleHierarchy: {
        ROLE_SKOLEO_CORE_ADMIN: [
          'ADMIN',
          'ROLE_SKOLEO_CORE_READ_ONLY_ADMIN',
          'ROLE_SKOLEO_CORE_COORDINATOR',
          'ROLE_SKOLEO_CORE_SCHOOLCONTACT',
        ],
        ROLE_SKOLEO_CORE_READ_ONLY_ADMIN: 'READ_ONLY_ADMIN',
        ROLE_SKOLEO_CORE_COORDINATOR: 'COORDINATOR',
        ROLE_SKOLEO_CORE_SCHOOLCONTACT: 'SCHOOL_CONTACT',
      },
      userService: UserService,
    }),
    provideStore(
      {
        '[bootstrap] router': routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictActionWithinNgZone: false,
          strictActionTypeUniqueness: false,
        },
      },
    ),
    provideRouterStore(),
    environment.environment !== SentryEnvironment.PRODUCTION
      ? provideStoreDevtools({
          name: 'school-dashboard',
          maxAge: 100, // Retains last 100 states
          logOnly: environment.environment !== SentryEnvironment.LOCAL, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
          trace: true, //environment.environment === SentryEnvironment.LOCAL,
          traceLimit: 75,
          connectInZone: false,
        })
      : [],
    provideNotifications(),
    provideUsersStore(),
  ],
}).catch((err: unknown) => console.error(err));
